/**
 * SuperAdmin Overview — Page 1
 * Platform-level health snapshot for superadmin console.
 * KPI data: GET /api/superadmin/overview (live)
 * System health + alerts: static until Phase 9 AI monitor lands.
 */

import React, { useState, useEffect } from 'react';
import { getSuperAdminOverview } from '../../utils/api';
import {
  FiHome, FiDollarSign, FiUsers, FiZap,
  FiCheckCircle, FiAlertTriangle, FiInfo, FiRefreshCw, FiLoader,
} from 'react-icons/fi';

// Static until Phase 9 AI monitor — no backend data for these yet
const STATIC_HEALTH = [
  { label: 'API response time', value: '142ms',     pct: 88,  color: '#27ae60', note: 'p95'     },
  { label: 'DPDPA score',       value: '92%',       pct: 92,  color: '#27ae60', note: ''        },
  { label: 'Test suite',        value: '278 / 278', pct: 100, color: '#27ae60', note: 'passing' },
  { label: 'Claude API cost',   value: '₹34.50',    pct: 35,  color: '#e67e22', note: 'today'   },
];

// Static until Phase 9 alerting service
const STATIC_ALERTS = [
  { id: 'a1', text: '2 pending payouts awaiting approval',   type: 'warning' },
  { id: 'a2', text: '1 agent escalation from Lotus Healing', type: 'error'   },
  { id: 'a3', text: '3 subscriptions renewing in 7 days',    type: 'info'    },
];

// KPI template — icons and colours fixed; values come from the API.
const KPI_TEMPLATE = [
  { label: 'Total centres',   key: 'activeCentres',      sub: 'active',           icon: FiHome,       color: '#1a6b5a' },
  { label: 'Platform MRR',    key: 'platformMRR',        sub: 'across all plans', icon: FiDollarSign, color: '#2980b9' },
  { label: 'Active patients', key: 'activePatients',     sub: 'all tenants',      icon: FiUsers,      color: '#8e44ad' },
  { label: 'Agent sessions',  key: 'agentSessionsToday', sub: 'today',            icon: FiZap,        color: '#e67e22' },
];

const STATUS_STYLES = {
  pending:  { bg: 'rgba(230,126,34,0.1)',  color: '#e67e22', label: 'Pending'  },
  approved: { bg: 'rgba(39,174,96,0.1)',   color: '#27ae60', label: 'Approved' },
  rejected: { bg: 'rgba(231,76,60,0.1)',   color: '#e74c3c', label: 'Rejected' },
};

const ALERT_STYLES = {
  warning: { bg: 'rgba(230,126,34,0.08)', color: '#e67e22', icon: FiAlertTriangle },
  error:   { bg: 'rgba(231,76,60,0.08)',  color: '#e74c3c', icon: FiAlertTriangle },
  info:    { bg: 'rgba(41,128,185,0.08)', color: '#2980b9', icon: FiInfo          },
};

// ─── Helpers ───────────────────────────────────────────────────────────────

function formatMRR(n) {
  return '₹' + Number(n || 0).toLocaleString('en-IN');
}

function relativeTime(iso) {
  const diff = Math.floor((Date.now() - new Date(iso)) / 1000);
  if (diff < 60)    return 'Just now';
  if (diff < 3600)  return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

function kpiValue(key, data) {
  if (key === 'platformMRR') return formatMRR(data[key]);
  return String(data[key] ?? 0);
}

// ─── Responsive helpers ────────────────────────────────────────────────────
// Inline styles can't do media queries — inject once, idempotent.
const STYLE_ID = 'sa-overview-responsive';
if (!document.getElementById(STYLE_ID)) {
  const el = document.createElement('style');
  el.id = STYLE_ID;
  el.textContent = `
    @media (max-width: 600px) { .sa-kpi-grid { grid-template-columns: repeat(2, 1fr) !important; } }
    @media (max-width: 860px) { .sa-two-cols { grid-template-columns: 1fr !important; } }
    @keyframes sa-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
  `;
  document.head.appendChild(el);
}

// ─── Styles ────────────────────────────────────────────────────────────────

const S = {
  kpiGrid: { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px', marginBottom: '24px' },

  kpiCard: (color) => ({
    background: '#fff', borderRadius: '10px', padding: '20px',
    borderTop: `3px solid ${color}`, boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
  }),

  kpiIcon: (color) => ({
    width: '36px', height: '36px', borderRadius: '8px',
    background: `${color}18`,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: '16px', color, marginBottom: '12px',
  }),

  kpiValue: { fontSize: '24px', fontWeight: '800', color: '#1a1a2e', lineHeight: 1 },
  kpiLabel: { fontSize: '12px', color: '#888', marginTop: '4px' },
  kpiSub:   { fontSize: '11px', color: '#aaa', marginTop: '2px' },

  cols: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '24px' },

  card: { background: '#fff', borderRadius: '10px', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', overflow: 'hidden' },

  cardHeader: {
    padding: '16px 20px 12px', borderBottom: '1px solid #f0f0f0',
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
  },

  cardTitle: { fontSize: '13px', fontWeight: '700', color: '#1a1a2e', display: 'flex', alignItems: 'center', gap: '8px' },

  queueBadge: {
    fontSize: '10px', fontWeight: '700', borderRadius: '9px', padding: '2px 7px',
    background: 'rgba(230,126,34,0.12)', color: '#e67e22',
  },

  viewAll: {
    fontSize: '12px', fontWeight: '600', color: '#1a6b5a',
    cursor: 'pointer', background: 'none', border: 'none', padding: '0 4px',
    minHeight: '44px', minWidth: '44px', display: 'flex', alignItems: 'center',
  },

  emptyQueue: { padding: '20px', textAlign: 'center', fontSize: '12px', color: '#bbb' },

  queueRow: {
    padding: '12px 20px', borderBottom: '1px solid #f6f6f6',
    display: 'flex', alignItems: 'center', gap: '10px',
  },

  queueName: { fontSize: '13px', fontWeight: '600', color: '#1a1a2e', flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  queueCity: { fontSize: '11px', color: '#999' },
  queueTime: { fontSize: '11px', color: '#bbb', whiteSpace: 'nowrap' },

  statusBadge: (status) => ({
    fontSize: '10px', fontWeight: '700', borderRadius: '5px', padding: '2px 7px',
    background: STATUS_STYLES[status]?.bg || '#f0f0f0',
    color:      STATUS_STYLES[status]?.color || '#888',
    whiteSpace: 'nowrap',
  }),

  healthRow: { padding: '10px 20px' },
  healthTop: { display: 'flex', justifyContent: 'space-between', marginBottom: '5px' },
  healthLabel: { fontSize: '12px', color: '#555', fontWeight: '500' },
  healthValue: { fontSize: '12px', fontWeight: '700', color: '#1a1a2e' },

  barTrack: { height: '6px', borderRadius: '3px', background: '#f0f0f0', overflow: 'hidden' },
  barFill: (pct, color) => ({
    height: '100%', borderRadius: '3px', width: `${pct}%`,
    background: color, transition: 'width 0.4s ease',
  }),
};

// ─── Loading / Error sub-views ─────────────────────────────────────────────

const LoadingView = () => (
  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '300px', gap: '10px', color: '#888' }}>
    <FiLoader style={{ fontSize: '20px', animation: 'sa-spin 1s linear infinite' }} />
    <span style={{ fontSize: '13px' }}>Loading overview…</span>
  </div>
);

const ErrorView = ({ message, onRetry }) => (
  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '300px', gap: '14px', textAlign: 'center', padding: '24px' }}>
    <div style={{ fontSize: '13px', color: '#e74c3c', maxWidth: '320px', lineHeight: '1.5' }}>{message}</div>
    <button
      type="button"
      onClick={onRetry}
      style={{
        display: 'flex', alignItems: 'center', gap: '6px',
        padding: '9px 18px', borderRadius: '7px', border: 'none',
        background: '#1a6b5a', color: '#fff', fontSize: '12px', fontWeight: '700',
        cursor: 'pointer', minHeight: '44px',
      }}
    >
      <FiRefreshCw style={{ fontSize: '13px' }} /> Retry
    </button>
  </div>
);

// ─── Component ─────────────────────────────────────────────────────────────

const Overview = ({ onNavigate }) => {
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);
  const [data,    setData]    = useState(null);

  const fetchOverview = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getSuperAdminOverview();
      setData(res.data.data);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load overview. Check your connection.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchOverview(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) return <LoadingView />;
  if (error)   return <ErrorView message={error} onRetry={fetchOverview} />;

  const { pendingQueue = [], pendingApplications = 0 } = data;

  return (
    <div>
      {/* KPI row — values from API, 4-across desktop, 2×2 at <600px */}
      <div style={S.kpiGrid} className="sa-kpi-grid">
        {KPI_TEMPLATE.map(({ label, key, sub, icon: Icon, color }) => (
          <div key={label} style={S.kpiCard(color)}>
            <div style={S.kpiIcon(color)}><Icon /></div>
            <div style={S.kpiValue}>{kpiValue(key, data)}</div>
            <div style={S.kpiLabel}>{label}</div>
            <div style={S.kpiSub}>{sub}</div>
          </div>
        ))}
      </div>

      {/* Two-column section — stacks to 1-col at <860px */}
      <div style={S.cols} className="sa-two-cols">

        {/* Left: live pendingQueue from API */}
        <div style={S.card}>
          <div style={S.cardHeader}>
            <span style={S.cardTitle}>
              Onboarding queue
              {pendingApplications > 0 && (
                <span style={S.queueBadge}>{pendingApplications} pending</span>
              )}
            </span>
            <button style={S.viewAll} type="button" onClick={() => onNavigate('onboarding')}>
              View all →
            </button>
          </div>
          {pendingQueue.length === 0
            ? <div style={S.emptyQueue}>No pending applications</div>
            : pendingQueue.map(row => (
                <div key={row._id} style={S.queueRow}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={S.queueName} title={row.centreName}>{row.centreName}</div>
                    <div style={S.queueCity}>{row.city}</div>
                  </div>
                  <span style={S.queueTime}>{relativeTime(row.submittedAt)}</span>
                  <span style={S.statusBadge(row.status)}>{STATUS_STYLES[row.status]?.label}</span>
                </div>
              ))
          }
        </div>

        {/* Right: static health + alerts (Phase 9 AI monitor will replace) */}
        <div style={S.card}>
          <div style={S.cardHeader}>
            <span style={S.cardTitle}>System health</span>
            <FiCheckCircle style={{ color: '#27ae60', fontSize: '16px' }} />
          </div>

          {STATIC_HEALTH.map(({ label, value, pct, color, note }) => (
            <div key={label} style={S.healthRow}>
              <div style={S.healthTop}>
                <span style={S.healthLabel}>{label}</span>
                <span style={S.healthValue}>
                  {value}
                  {note && <span style={{ fontWeight: '400', color: '#aaa', marginLeft: '4px' }}>{note}</span>}
                </span>
              </div>
              <div style={S.barTrack}>
                <div style={S.barFill(pct, color)} />
              </div>
            </div>
          ))}

          {/* Recent alerts */}
          <div style={{ padding: '14px 20px 10px', borderTop: '1px solid #f0f0f0', marginTop: '6px' }}>
            <div style={{ fontSize: '12px', fontWeight: '700', color: '#1a1a2e', marginBottom: '10px' }}>
              Recent alerts
            </div>
            {STATIC_ALERTS.map(({ id, text, type }) => {
              const { icon: AlertIcon, color } = ALERT_STYLES[type];
              return (
                <div key={id} style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', marginBottom: '10px' }}>
                  <AlertIcon style={{ color, fontSize: '14px', marginTop: '1px', flexShrink: 0 }} />
                  <span style={{ fontSize: '12px', color: '#444', lineHeight: '1.4' }}>{text}</span>
                </div>
              );
            })}
          </div>
        </div>

      </div>
    </div>
  );
};

export default Overview;
